import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Button, Flex, Modal, Space, Typography } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { useWarnAboutChange } from "@refinedev/core";
import { UNSAFE_NavigationContext, useLocation } from "react-router-dom";

/**
 * Cómo guarda la pantalla abierta, si sabe: el popup lo ofrece como tercer botón. Devuelve si
 * se pudo guardar; si no, el popup queda abierto y la pantalla ya dijo por qué.
 */
type Guardado = { texto: string; guardar: () => Promise<boolean> };

/**
 * El guardado de la pantalla abierta. Vive afuera de React porque el popup y la pantalla son
 * hermanos —el popup va junto al router, no adentro de cada página— y no comparten un contexto.
 */
let guardadoActual: Guardado | null = null;
const suscriptores = new Set<() => void>();
const avisar = () => suscriptores.forEach((s) => s());
const suscribir = (s: () => void) => {
  suscriptores.add(s);
  return () => suscriptores.delete(s);
};

/**
 * **Una pantalla que sabe guardar se lo dice al popup** (xcons-docs#490): mientras esté montada,
 * el popup de cambios sin guardar suma el botón con `texto`, que guarda y después sale.
 */
export const useGuardarAlSalir = (texto: string, guardar: () => Promise<boolean>) => {
  // La función cambia en cada render (lee el estado de la pantalla): se usa siempre la última.
  const ultima = useRef(guardar);
  ultima.current = guardar;
  useEffect(() => {
    const propio: Guardado = { texto, guardar: () => ultima.current() };
    guardadoActual = propio;
    avisar();
    return () => {
      if (guardadoActual === propio) {
        guardadoActual = null;
        avisar();
      }
    };
  }, [texto]);
};

/**
 * El aviso de cambios sin guardar **como popup del admin, no del navegador** (xcons-docs#490).
 * Reemplaza al `UnsavedChangesNotifier` de refine, que preguntaba con `window.confirm`.
 *
 * Usa el mismo mecanismo que refine —intercepta `push` y `go` del navegador del router, porque
 * con `BrowserRouter` no hay `useBlocker`—, pero en vez de preguntar en el momento guarda la
 * navegación y la hace recién si se elige salir.
 *
 * **Cerrar la pestaña o recargar sigue con el aviso del navegador**: ahí la página no puede
 * mostrar nada propio, sólo pedirle al navegador que pregunte.
 */
export const AvisoDeCambiosSinGuardar: React.FC = () => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const { pathname } = useLocation();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const guardado = useSyncExternalStore(suscribir, () => guardadoActual);
  /** La navegación que quedó esperando la respuesta. */
  const pendiente = useRef<(() => void) | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Al cambiar de pantalla, lo sin guardar de la anterior ya no cuenta.
  useEffect(() => () => setWarnWhen(false), [pathname, setWarnWhen]);

  useEffect(() => {
    if (!warnWhen) return;
    const alCerrar = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", alCerrar);

    const push = navigator.push;
    const go = navigator.go;
    navigator.push = (...args: Parameters<typeof push>) => {
      pendiente.current = () => push(...args);
      setAbierto(true);
    };
    navigator.go = (...args: Parameters<typeof go>) => {
      pendiente.current = () => go(...args);
      setAbierto(true);
    };
    return () => {
      window.removeEventListener("beforeunload", alCerrar);
      navigator.push = push;
      navigator.go = go;
    };
  }, [navigator, warnWhen]);

  const salir = useCallback(() => {
    const navegar = pendiente.current;
    pendiente.current = null;
    setAbierto(false);
    setWarnWhen(false);
    // Después de apagar el aviso, para que la navegación no vuelva a quedar interceptada.
    setTimeout(() => navegar?.(), 0);
  }, [setWarnWhen]);

  const guardarYSalir = async () => {
    if (!guardado) return;
    setGuardando(true);
    try {
      if (await guardado.guardar()) salir();
    } finally {
      setGuardando(false);
    }
  };

  const quedarse = () => {
    pendiente.current = null;
    setAbierto(false);
  };

  return (
    <ModalDeCambiosSinGuardar
      open={abierto}
      onSalir={salir}
      onQuedarse={quedarse}
      guardar={
        guardado
          ? { texto: guardado.texto, onClick: () => void guardarYSalir(), cargando: guardando }
          : undefined
      }
    />
  );
};

/**
 * El popup de cambios sin guardar, el mismo de la ficha de producto: Salir sin guardar solo a
 * la izquierda, porque es lo que pierde el trabajo; a la derecha los que lo conservan —guardar,
 * si la pantalla sabe, y Seguir editando—.
 */
export const ModalDeCambiosSinGuardar: React.FC<{
  open: boolean;
  onSalir: () => void;
  onQuedarse: () => void;
  /** El texto del botón que sale. */
  textoSalir?: string;
  /** El botón que guarda y sale, si la pantalla sabe guardar. */
  guardar?: { texto: string; onClick: () => void; cargando?: boolean };
}> = ({ open, onSalir, onQuedarse, textoSalir = "Salir sin guardar", guardar }) => (
  <Modal
    open={open}
    title={
      <Space size={8}>
        <ExclamationCircleFilled style={{ color: "#faad14" }} />
        Tenés cambios sin guardar
      </Space>
    }
    onCancel={onQuedarse}
    // Más ancho que los 520px de antd: con los tres botones al pie quedaba apretado.
    width={guardar ? 560 : undefined}
    styles={{ body: { padding: "24px 0 36px" } }}
    footer={
      <Flex justify="space-between" gap={8}>
        <Button danger onClick={onSalir}>
          {textoSalir}
        </Button>
        <Flex gap={8}>
          {guardar && (
            <Button loading={guardar.cargando} onClick={guardar.onClick}>
              {guardar.texto}
            </Button>
          )}
          <Button type="primary" onClick={onQuedarse}>
            Seguir editando
          </Button>
        </Flex>
      </Flex>
    }
  >
    <Typography.Text>
      Si salís sin guardar, se pierden los cambios que hiciste.
    </Typography.Text>
  </Modal>
);
